import React, { useState, useEffect } from "react";
import axios from "axios";
import { Form, Button, Table } from "react-bootstrap";
import appConstants from "../constants/appConstants";
import Layout from "../components/Layout";
import { FaPlus, FaTrash } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

const getAuthToken = () => localStorage.getItem("authToken");

const EditJournal = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [entries, setEntries] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [date, setDate] = useState("");
    const [description, setDescription] = useState("");

    useEffect(() => {
        fetchAccounts();
        fetchJournal();
    }, []);

    const fetchAccounts = async () => {
        const token = getAuthToken();
        if (!token) return alert("Unauthorized! Please log in.");
        try {
            const response = await axios.get(`${appConstants.API_ROOT_URL}get-accounts`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAccounts(response.data.data);
        } catch (error) {
            console.error("Error fetching accounts:", error);
        }
    };

    const fetchJournal = async () => {
        const token = getAuthToken();
        if (!token) return alert("Unauthorized! Please log in.");
        try {
            const response = await axios.get(`${appConstants.API_ROOT_URL}get-one-journal?journal_id=${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status !== "success") throw new Error(response.data.message);

            const journal = response.data.journal;
            setDate(journal.date || "");
            setDescription(journal.description || "");
            setEntries(journal.entries.map(entry => ({
                account_id: entry.account_id || "",
                account_name: entry.account_name || "",
                debit: entry.debit_credit === "D" ? entry.amount : "",
                credit: entry.debit_credit === "C" ? entry.amount : ""
            })));
        } catch (error) {
            console.error("Error fetching journal:", error);
            alert(error.message || "An error occurred while fetching the journal.");
        }
    };

    const handleAccountSelection = (index, value) => {
        const selectedAccount = accounts.find(acc => acc.name === value);
        const updatedEntries = [...entries];
        if (selectedAccount) {
            updatedEntries[index].account_id = selectedAccount.id;
            updatedEntries[index].account_name = selectedAccount.name;
        } else {
            updatedEntries[index].account_id = "";
            updatedEntries[index].account_name = value;
        }
        setEntries(updatedEntries);
    };

    const handleChange = (index, field, value) => {
        const updatedEntries = [...entries];
        updatedEntries[index][field] = value;
        setEntries(updatedEntries);
    };

    const addEntry = () => {
        setEntries([...entries, { account_id: "", account_name: "", debit: "", credit: "" }]);
    };

    const removeEntry = (index) => {
        const updatedEntries = [...entries];
        updatedEntries.splice(index, 1);
        setEntries(updatedEntries);
    };

    const calculateTotals = () => {
        let totalDebit = 0, totalCredit = 0;
        entries.forEach(entry => {
            totalDebit += parseFloat(entry.debit || 0);
            totalCredit += parseFloat(entry.credit || 0);
        });
        return { totalDebit, totalCredit };
    };

    const { totalDebit, totalCredit } = calculateTotals();
    const isBalanced = totalDebit === totalCredit && totalDebit > 0;

    const updateJournal = async () => {
        if (!isBalanced) {
            alert("Debits and Credits must be equal before submitting.");
            return;
        }
        const token = getAuthToken();
        if (!token) return alert("Unauthorized! Please log in.");

        const transformedEntries = entries.flatMap(entry => {
            const formattedEntries = [];
            if (entry.debit && parseFloat(entry.debit) > 0) {
                formattedEntries.push({
                    account_id: entry.account_id,
                    amount: parseFloat(entry.debit),
                    debit_credit: "D"
                });
            }
            if (entry.credit && parseFloat(entry.credit) > 0) {
                formattedEntries.push({
                    account_id: entry.account_id,
                    amount: parseFloat(entry.credit),
                    debit_credit: "C"
                });
            }
            return formattedEntries;
        });

        try {
            const response = await axios.post(
                `${appConstants.API_ROOT_URL}update-journal`,
                {
                    journal_id: id,
                    journal: {
                        date,
                        description
                    },
                    entries: transformedEntries
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            if (response.data.status === "success") {
                alert("Journal updated successfully!");
                navigate("/journal-list");
            } else {
                alert(`Error: ${response.data.message}`);
            }
        } catch (error) {
            alert("Error updating journal entry");
            console.error(error);
        }
    };

    return (
        <Layout>
            <div className="p-3 bg-light">
                <h5>Edit Journal Entry</h5>
                <Form>
                    <Form.Group className="mb-2">
                        <Form.Label>Date</Form.Label>
                        <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>Description</Form.Label>
                        <Form.Control type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </Form.Group>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Account</th>
                                <th>Debit</th>
                                <th>Credit</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry, index) => (
                                <tr key={index}>
                                    <td>
                                        <Form.Control 
                                            list="accounts"
                                            value={entry.account_name}
                                            onChange={(e) => handleAccountSelection(index, e.target.value)}
                                        />
                                        <datalist id="accounts">
                                            {accounts.map(account => (
                                                <option key={account.id} value={account.name} />
                                            ))}
                                        </datalist>
                                    </td>
                                    <td>
                                        <Form.Control type="number" value={entry.debit} onChange={(e) => handleChange(index, "debit", e.target.value)} />
                                    </td>
                                    <td>
                                        <Form.Control type="number" value={entry.credit} onChange={(e) => handleChange(index, "credit", e.target.value)} />
                                    </td>
                                    <td>
                                        {entries.length > 1 && (
                                            <Button variant="danger" size="sm" onClick={() => removeEntry(index)}>
                                                <FaTrash />
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <div className="d-flex justify-content-between">
                        <strong>Total Debit: {totalDebit}</strong>
                        <strong>Total Credit: {totalCredit}</strong>
                    </div>
                    <Button variant="success" className="mt-2" onClick={addEntry}>
                        <FaPlus /> Add Entry
                    </Button>
                    <Button variant="primary" className="mt-2 ms-2" onClick={updateJournal} disabled={!isBalanced}>
                        Update Journal Entry
                    </Button>
                    <Button variant="secondary" className="mt-2 ms-2" onClick={() => navigate("/journal-list")}>
                        Cancel
                    </Button>
                </Form>
            </div>
        </Layout>
    );
};

export default EditJournal;
